


import React, { ReactNode, useState, forwardRef } from 'react'

import { FieldValues, RegisterOptions, get } from 'react-hook-form'

import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { FormControl, FormHelperText, type InputProps, useTheme, IconButton, InputAdornment, Stack } from '@mui/material'
import type { SxProps } from '@mui/material'

import { Input, FieldLabel } from '@repo/ui'

import { FieldProps } from './types'

interface TextFieldProps<T extends FieldValues = FieldValues>
  extends Omit<FieldProps<T>, 'startAdornment'> {
  name: string
  label?: string
  required?: boolean
  placeholder?: string
  defaultValue?: string
  value?: string
  type?: string
  helperText?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  /**
   * Inline content beside the label — in practice an `InfoTooltip`.
   *
   * Declared since this component was written and never read: it type-checked at every
   * call site and rendered nothing, which is the quiet kind of dead prop, since the
   * author sees no error and no output. Wired rather than deleted because §67 needs it —
   * a settings field whose effect lands on a surface the editor cannot see owes a
   * tooltip beside its LABEL, and the label belongs to `FieldLabel` (§31), not to the
   * page. Pulling the label out to the caller to hang a glyph on it is the §31 violation
   * that puts the parent Stack's spacing between a label and its control.
   *
   * Composed WITH the `(optional)`/`(required)` state marker rather than replacing it: a
   * field can legitimately be optional and still need to say where it takes effect,
   * which is exactly the case on Settings → Fields → Defaults.
   */
  labelAdornment?: ReactNode
  rules?: RegisterOptions
  readOnly?: boolean
  sx?: SxProps
  inputProps?: InputProps['inputProps']
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onValueChange?: (value: string) => void
  /**
   * Runs on blur IN ADDITION to react-hook-form's own blur handler, which this
   * component must keep calling (it drives touched state and onBlur validation).
   *
   * Declared explicitly because it previously wasn't: `onBlur` was hardcoded to
   * `registerProps.onBlur`, so a caller passing one had it silently dropped —
   * no type error, no runtime warning, just a handler that never fired. Use this
   * for side effects that belong to blur, e.g. an availability probe.
   */
  onFieldBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  labelVariant?: 'body1' | 'body2' | 'body3' | 'body4'
  /**
   * Render a `<textarea>` instead of an `<input>`, for a value that is a sentence or two rather
   * than a single token.
   *
   * Added HERE rather than by reaching for the sibling `TextAreaField` (§47 — improve the shared
   * thing before building around it). `TextAreaField` cannot serve a form whose values arrive
   * async: it is uncontrolled (`defaultValue` only, no `value`), so a `setValue` after the record
   * loads never reaches the DOM and the field renders empty over populated form state. It also
   * hand-rolls its label as a bare `<Typography mb="8px">` instead of `FieldLabel`, which is the
   * §31 violation this component does not have, and it hangs `helperText` BELOW the control where
   * every other field carries it as a label adornment.
   *
   * So the multiline case joins the field that already gets the label, the border, the controlled
   * value and the RHF ref wiring right. `@repo/ui`'s `Input` already accepted these three props;
   * they were simply never forwarded.
   */
  multiline?: boolean
  minRows?: number
  maxRows?: number
}

const TextFieldImpl = forwardRef<HTMLInputElement, TextFieldProps>(({
  label,
  required,
  name,
  placeholder,
  startAdornment,
  endAdornment,
  rules,
  register,
  helperText,
  formState: { errors },
  readOnly = false,
  defaultValue = '',
  value,
  type = 'text',
  sx,
  inputProps,
  handleKeyDown,
  onValueChange,
  onFieldBlur,
  labelVariant = 'body3',
  labelAdornment,
  multiline = false,
  minRows,
  maxRows,
}, ref) => {

  const theme = useTheme()
  const registerProps = register(name, rules);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const renderEndAdornment = () => {
    if (type == 'password') {
      return (
        <InputAdornment position="end" 
          sx={{
            pr: 2,
            '& :hover': {
              backgroundColor: 'transparent', 
            }
          }}>
          <IconButton
            aria-label={
              showPassword ? 'hide the password' : 'display the password'
            }
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            onMouseUp={handleMouseUpPassword}
            edge="end"
          >
            {showPassword ? <EyeOffIcon strokeWidth={1}/> : <EyeIcon strokeWidth={1} />}
          </IconButton>
        </InputAdornment>
      )
    }
    return endAdornment
  }

  /**
   * The height a `minRows` textarea should have, applied ourselves.
   *
   * `minRows` is the correct API and IS reaching MUI — verified on the fibre tree, TextareaAutosize
   * receives `minRows: 3`. It just does nothing: the element renders `rows="2"` with no inline
   * height, so the auto-sizing effect never completes. This is NOT specific to this field or this
   * change — the support form asks for `minRows={5}` and renders a 40px box, under two lines. The
   * cause is almost certainly the TWO @mui/material copies in the pnpm store that ScanMediaField
   * and FileUploadField already document (tracked as VRG-315): InputBase resolves TextareaAutosize
   * from the other copy, and the height sync never lands.
   *
   * So `minRows` is still passed through (it is the right prop, and this stops being needed the day
   * the duplicate is resolved) and the height is ALSO stated here, on the input slot, so the field
   * is the size it claims to be today. `1.4375em` is MUI's own input line-height, so this tracks
   * the font rather than hardcoding the 23px it currently computes to.
   *
   * `minHeight` rather than `height` so that the day the duplicate is resolved and auto-sizing
   * starts working, growth past the floor happens instead of being clamped. Measured behaviour
   * TODAY is a fixed box that SCROLLS past `minRows`: typing four lines into a `minRows={3}` field
   * leaves it at 69px. That is `rows={3}` semantics, which is a reasonable field, but it is not
   * auto-growing and this comment should not be read as claiming it is.
   *
   * `:not([aria-hidden])` is LOAD-BEARING, not tidiness. TextareaAutosize renders TWO textareas —
   * the visible one and an aria-hidden shadow it measures content against — and BOTH carry
   * `.MuiInputBase-inputMultiline`. Without the exclusion the floor also applies to the shadow, so
   * the shadow reports 69px of "content", MUI multiplies that through its own sizing, and the field
   * renders 207px — three times the intended height, from a rule that looks correct. Measured.
   *
   * Merged over the caller's `sx` rather than replacing it — which also fixes `sx` being a DEAD
   * PROP on this component until now: it was destructured and then never forwarded, so every
   * caller that passed one was silently ignored.
   */
  const multilineSx = multiline && minRows
    ? {
        ...(sx as object | undefined),
        '& .MuiInputBase-inputMultiline:not([aria-hidden])': {
          minHeight: `calc(${minRows} * 1.4375em)`,
        },
      }
    : (sx as object | undefined)

  const renderType = () => {
    if (type == 'password' && showPassword) return 'text' 
    if (type == 'password' && !showPassword) return 'password' 
    return type
  }

  /*
    The two message slots, and the rule that keeps them from saying the same thing.

    A field has two places text can appear: the LABEL ADORNMENT (beside the label) and
    the HELPER SLOT (under the control). They answer different questions —

      adornment   = persistent state.      "this field is required" / "(optional)"
      helper slot = a validation message.  "here is what is wrong right now"

    …and for a REQUIRED error those two collapse onto the same sentence. That is what
    shipped: `(This field is required)` beside the label AND `This field is required`
    below it, because several call sites pipe `fieldState.error?.message` straight into
    `helperText` while the helper slot below already renders `errors[name].message` from
    `formState` on its own. One message, authored once, rendered twice.

    So:
      - the adornment never carries a validation message. While a field is erroring it
        shows the short `(required)` marker if the field is required, and otherwise
        nothing — the reason belongs below, once.
      - the helper slot suppresses ONLY the required error. Every other reason (too
        long, bad URL, subdomain taken) still renders there, because for those the
        helper slot is the only place the reason can live. Suppressing the slot wholesale
        would leave a field that refuses to submit and says nothing about why — strictly
        worse than the duplication. `required` is the one reason a four-character marker
        can carry in full; nothing else is.

    Gated on `error.type`, not on the message text: string-matching the copy breaks the
    moment someone rewords `fieldCannotBeEmpty`.
  */
  // `get`, not `errors[name]`: `errors` is NESTED, so a dotted name such as
  // `contact_card.socials.2.url` never resolved by index. Such a field refused to submit
  // with no red border and no message below; the reason leaked out only as grey hint text
  // beside the label (VRG-720, found in the browser).
  const fieldError = get(errors, name) as { type?: string; message?: string } | undefined
  const isRequiredError = fieldError?.type === 'required'

  /*
    `required`/`rules` make the marker PERSISTENT for call sites that declare it; the
    error itself is the fallback signal for the many that register their rules on a
    wrapping `<Controller>` instead, where this component never sees them.

    Gated on `label` because a marker with no label has nothing to attach to. Caught in
    the browser rather than reasoned about: the Inventory `quantity` stepper passes no
    `label` (the page hand-rolls its "Quantity" heading), so the marker rendered ALONE on
    its own line under that heading instead of inline beside it, where every `(optional)`
    sits.
  */
  const showRequiredMarker =
    Boolean(label) && (Boolean(required) || Boolean(rules?.required) || isRequiredError)

  const adornmentText = fieldError
    ? (showRequiredMarker ? 'required' : undefined)
    : (helperText ?? (showRequiredMarker ? 'required' : undefined))

  // The message below is dropped ONLY when the marker is actually rendered to replace it.
  // Tying the two together is what stops a labelless required field losing BOTH — which a
  // bare `isRequiredError` check would have done, leaving no error signal at all.
  const suppressRequiredMessage = isRequiredError && showRequiredMarker

  /*
    The label row's inline slot, which now has two possible occupants (see
    `labelAdornment`). Built here rather than inline in the JSX so the "neither is
    present" case stays `undefined` — `FieldLabel` returns null on a label-less,
    adornment-less row, and handing it an always-present empty Stack would defeat that.
  */
  const stateMarker = adornmentText
    ? <FormHelperText error={isRequiredError} sx={{ m: 0 }}>({adornmentText})</FormHelperText>
    : null
  const composedAdornment = stateMarker || labelAdornment
    ? (
      <Stack direction="row" alignItems="center" spacing={1}>
        {stateMarker}
        {labelAdornment}
      </Stack>
    )
    : undefined

  return (
    <FormControl error={!!fieldError} fullWidth>
      <FieldLabel
        label={label}
        variant={labelVariant}
        /*
          The marker goes RED exactly when it IS the active required error, and stays
          neutral otherwise. This is not decoration — it is what makes suppressing the
          message below safe.

          THE ORIGINAL REASONING HERE IS NOW HALF-OBSOLETE, and it matters which half.
          When this was written, `@repo/ui`'s `Input` had NO `&.Mui-error` rule at all: its
          styled root set the border from `fieldBorderColor(theme)` unconditionally, and the
          `error` prop only tinted `Input`'s OWN `helperText`, which this component never
          passes (it renders its own `FormHelperText` below). An errored field's border
          computed to rgb(39,39,42) — the rest colour — so the red marker was the only thing
          on the field itself saying anything was wrong, and dropping the message below while
          the marker stayed muted would have left an invalid field pixel-identical to a valid
          one. `Input` now HAS that rule (red border, plus a red focus ring), so that specific
          emergency is over.

          THE MARKER STAYS RED ANYWAY, and not out of inertia. The border and the marker
          answer different questions: the border says WHICH field is blocking you, the marker
          says WHY. The border cannot carry the reason — it is the same red for "required",
          "too long", "bad URL", "already taken" — so with the message below suppressed, the
          marker is the only channel for it. Neutral marker + red border would be a field that
          is visibly broken and never says how to fix it, and a muted `(required)` beside a red
          field reads as persistent state unrelated to the error rather than as its cause.

          On whether three red things is too much: measured, they are not the same weight. The
          marker is 12px of text ~53px wide inside the label; the border is a 1px outline around
          a 202px control; the focus ring is 6% alpha and is imperceptible in practice. That is a
          locator and a caption, not three competing alarms. `(optional)` and descriptive hints
          stay neutral, since they are never errors.
        */
        adornment={composedAdornment}
      />
      <Input
        // RHF's ref goes on the INPUT, and it is not optional decoration: on blur,
        // RHF re-reads the field's value from this node rather than from the event
        // (`getFieldValue(field._f)`). With no node registered it reads `undefined`
        // and overwrites whatever onChange had stored — so a field would accept
        // typing and then silently empty itself the moment focus left, which is what
        // made "Email is required" fire against a filled-in input.
        inputRef={(el: HTMLInputElement | null) => {
          registerProps.ref(el)
          if (typeof ref === 'function') ref(el)
          else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
        }}
        name={name}
        required={required}
        type={renderType()}
        // Undefined is deliberate rather than a local default: `Input` owns minRows=2 / maxRows=10,
        // and repeating those numbers here would be a second place for them to drift.
        multiline={multiline}
        minRows={multiline ? minRows : undefined}
        maxRows={multiline ? maxRows : undefined}
        sx={multilineSx}
        placeholder={placeholder}
        startAdornment={startAdornment}
        endAdornment={renderEndAdornment()}
        // Read-only, not disabled: a prefilled invited-signup email (VERA-1095)
        // still needs to be legible and selectable, which `disabled`'s 0.4-opacity
        // fade and keyboard-nav skip both defeat. `@repo/ui` Input's own `readOnly`
        // mode already keeps full contrast, a default cursor and no focus border —
        // this wrapper was just never passing it through (it silently mapped
        // `readOnly` to `disabled` instead, which no existing caller had actually
        // exercised).
        readOnly={readOnly}
        {...(value !== undefined ? { value } : { defaultValue })}
        error={!!fieldError}
        inputProps={inputProps}
        onValueChange={onValueChange}
        onChange={(value: string) => {
          // Convert the string value to an event-like object for react-hook-form.
          //
          // `name` is NOT optional here, though nothing type-checks it: RHF looks the
          // field up by `event.target.name` and, finding none, skips the update
          // entirely — no warning, no error. The field then holds text on screen while
          // the form still considers it empty, so submitting reports "required" against
          // a visibly filled input. That was the sign-up page's failure.
          registerProps.onChange({ target: { name, value } });
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          // RHF's handler MUST still run — it drives touched state and onBlur
          // validation. The caller's is additive, never a replacement.
          registerProps.onBlur(e);
          onFieldBlur?.(e);
        }}
      />
      {/* See the two-slot note above: the required error is carried by the `(required)`
          marker plus the red border, so repeating it here is pure duplication. Every
          other reason still renders — it has nowhere else to go. */}
      {fieldError && !suppressRequiredMessage && <FormHelperText sx={{ margin: '4px 0 0' }}>{fieldError.message as string}</FormHelperText>}
    </FormControl>
  )
})

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression. `forwardRef` erases type parameters, so the ref is restored by hand here.
 */
export const TextField = TextFieldImpl as <T extends FieldValues = FieldValues>(
  props: TextFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement | null
